import { useState, useCallback, useRef } from 'react';
import {
  MAX_ATTEMPTS,
  TOTAL_TILES,
  generateLayout,
  getScore,
  spanToStage,
  type TileLayout,
} from '@/pages/home/gameConfig';

export interface AttemptResult {
  span: number;
  isRetry: boolean;
  isSuccess: boolean;
  presentedSequence: number[];
  userSequence: number[];
  touchReactionTimes: number[];
  attemptDuration: number;
}

export interface GameData {
  gameType: string;
  performedAt: string;
  results: AttemptResult[];
  maxSuccessSpan: number;
  maxSpanIsFirstTry: boolean;
  score: number;
  totalDuration: number;
  isAborted: boolean;
}

export type ScreenType = 'intro' | 'start' | 'tutorial' | 'game' | 'result';
export type TutorialPhase = 'showing' | 'input' | 'success' | 'fail';

function generateSequence(length: number, tileCount: number): number[] {
  const indices = Array.from({ length: tileCount }, (_, i) => i);
  const sequence: number[] = [];
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * indices.length);
    sequence.push(indices[randomIndex]);
    indices.splice(randomIndex, 1);
  }
  return sequence;
}

export function useGameState() {
  const [screen, setScreen] = useState<ScreenType>('intro');
  const [currentSpan, setCurrentSpan] = useState(3);
  const [isRetry, setIsRetry] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);
  const [sequence, setSequence] = useState<number[]>([]);
  const [userSequence, setUserSequence] = useState<number[]>([]);
  const [isShowing, setIsShowing] = useState(false);
  const [isInputEnabled, setIsInputEnabled] = useState(false);
  const [results, setResults] = useState<AttemptResult[]>([]);
  const [maxSuccessSpan, setMaxSuccessSpan] = useState(0);
  const [maxSpanIsFirstTry, setMaxSpanIsFirstTry] = useState(false);
  const [score, setScore] = useState(0);
  const [layout, setLayout] = useState<TileLayout>([]);
  const [tutorialPhase, setTutorialPhase] = useState<TutorialPhase>('showing');
  const [tutorialSequence] = useState<number[]>([0, 1, 2]);
  const [showingIndex, setShowingIndex] = useState(-1);
  const [showingStep, setShowingStep] = useState(-1);
  const [message, setMessage] = useState('');
  const [showExitModal, setShowExitModal] = useState(false);

  const roundStartTimeRef = useRef<number>(0);
  const gameStartTimeRef = useRef<number>(0);
  const touchTimesRef = useRef<number[]>([]);
  const isAnimatingRef = useRef(false);

  // 비동기 타임아웃에서 안전하게 읽기 위한 ref (stale closure 방지)
  const attemptCountRef = useRef(0);
  const currentSpanRef = useRef(3);
  const isRetryRef = useRef(false);
  const resultsRef = useRef<AttemptResult[]>([]);
  const maxSuccessSpanRef = useRef(0);
  const maxSpanIsFirstTryRef = useRef(false);

  const setSpan = useCallback((span: number) => {
    currentSpanRef.current = span;
    setCurrentSpan(span);
  }, []);

  // 현재 span으로 점등 애니메이션 + 입력 대기를 시작
  const startAttempt = useCallback(() => {
    const span = currentSpanRef.current;
    const newSequence = generateSequence(span, TOTAL_TILES);
    setSequence(newSequence);
    setUserSequence([]);
    setIsShowing(true);
    setIsInputEnabled(false);
    setShowingIndex(-1);
    setShowingStep(-1);
    setMessage('불이 켜지는 위치를 기억해주세요');
    isAnimatingRef.current = true;
    roundStartTimeRef.current = Date.now();
    touchTimesRef.current = [];

    let currentIndex = 0;

    const showNext = () => {
      if (currentIndex >= newSequence.length) {
        setShowingIndex(-1);
        setShowingStep(-1);
        setIsShowing(false);
        setIsInputEnabled(true);
        setMessage('이제 같은 순서대로 눌러주세요');
        isAnimatingRef.current = false;
        return;
      }
      setShowingIndex(newSequence[currentIndex]);
      setShowingStep(currentIndex);
      currentIndex++;
      setTimeout(() => {
        setShowingIndex(-1);
        setTimeout(showNext, 250);
      }, 600);
    };

    setTimeout(showNext, 600);
  }, []);

  const finishGame = useCallback((aborted = false) => {
    const totalDuration = Date.now() - gameStartTimeRef.current;
    const finalSpan = maxSuccessSpanRef.current;
    const isFirstTry = maxSpanIsFirstTryRef.current;
    const finalScore = getScore(finalSpan, isFirstTry);

    setScore(finalScore);
    setMaxSuccessSpan(finalSpan);
    setMaxSpanIsFirstTry(isFirstTry);

    const gameData: GameData = {
      gameType: 'spatial-span',
      performedAt: new Date().toISOString(),
      results: resultsRef.current,
      maxSuccessSpan: finalSpan,
      maxSpanIsFirstTry: isFirstTry,
      score: finalScore,
      totalDuration,
      isAborted: aborted,
    };
    console.log('Game Data:', gameData);
    setScreen('result');
  }, []);

  const goToTutorial = useCallback(() => {
    setScreen('tutorial');
    setTutorialPhase('showing');
    setUserSequence([]);
    setIsShowing(true);
    setIsInputEnabled(false);
    setShowingIndex(-1);
    setShowingStep(-1);
    setLayout(generateLayout());
    isAnimatingRef.current = true;

    const seq = [0, 1, 2];
    let currentIndex = 0;

    const showNext = () => {
      if (currentIndex >= seq.length) {
        setShowingIndex(-1);
        setShowingStep(-1);
        setIsShowing(false);
        setIsInputEnabled(true);
        setMessage('이제 같은 순서대로 눌러주세요');
        setTutorialPhase('input');
        isAnimatingRef.current = false;
        return;
      }
      setShowingIndex(seq[currentIndex]);
      setShowingStep(currentIndex);
      currentIndex++;
      setTimeout(() => {
        setShowingIndex(-1);
        setTimeout(showNext, 250);
      }, 600);
    };

    setTimeout(showNext, 800);
  }, []);

  const goToGame = useCallback(() => {
    setScreen('game');
    attemptCountRef.current = 0;
    setAttemptCount(0);
    setSpan(3);
    isRetryRef.current = false;
    setIsRetry(false);
    resultsRef.current = [];
    setResults([]);
    maxSuccessSpanRef.current = 0;
    maxSpanIsFirstTryRef.current = false;
    setMaxSuccessSpan(0);
    setMaxSpanIsFirstTry(false);
    gameStartTimeRef.current = Date.now();
    startAttempt();
  }, [setSpan, startAttempt]);

  const goToGameGuide = useCallback(() => {
    setScreen('start');
  }, []);

  const goToIntro = useCallback(() => {
    setScreen('intro');
  }, []);

  const openExitModal = useCallback(() => {
    setShowExitModal(true);
  }, []);

  const closeExitModal = useCallback(() => {
    setShowExitModal(false);
  }, []);

  const confirmExit = useCallback(() => {
    setShowExitModal(false);
    setScreen('intro');
    setSequence([]);
    setUserSequence([]);
    setIsShowing(false);
    setIsInputEnabled(false);
    setResults([]);
    setSpan(3);
    setIsRetry(false);
    attemptCountRef.current = 0;
    setAttemptCount(0);
    isRetryRef.current = false;
    resultsRef.current = [];
    maxSuccessSpanRef.current = 0;
    maxSpanIsFirstTryRef.current = false;
    setMaxSuccessSpan(0);
    setMaxSpanIsFirstTry(false);
    setScore(0);
    setShowingIndex(-1);
    setShowingStep(-1);
    setMessage('');
    setTutorialPhase('showing');
    isAnimatingRef.current = false;
  }, [setSpan]);

  const handleTutorialTilePress = useCallback((tileIndex: number) => {
    if (!isInputEnabled || isAnimatingRef.current || tutorialPhase !== 'input') return;

    const newUserSequence = [...userSequence, tileIndex];
    setUserSequence(newUserSequence);

    if (newUserSequence.length === tutorialSequence.length) {
      const isCorrect = newUserSequence.every((val, idx) => val === tutorialSequence[idx]);
      setIsInputEnabled(false);
      if (isCorrect) {
        setTutorialPhase('success');
        setMessage('좋아요! 이제 시작해볼까요?');
        setTimeout(() => {
          goToGame();
        }, 1500);
      } else {
        setTutorialPhase('fail');
        setMessage('다시 한 번 해볼까요?');
        setTimeout(() => {
          setUserSequence([]);
          setTutorialPhase('showing');
          setIsShowing(true);
          setIsInputEnabled(false);
          setShowingIndex(-1);
          setShowingStep(-1);
          isAnimatingRef.current = true;
          let currentIndex = 0;
          const showNext = () => {
            if (currentIndex >= tutorialSequence.length) {
              setShowingIndex(-1);
              setShowingStep(-1);
              setIsShowing(false);
              setIsInputEnabled(true);
              setMessage('이제 같은 순서대로 눌러주세요');
              setTutorialPhase('input');
              isAnimatingRef.current = false;
              return;
            }
            setShowingIndex(tutorialSequence[currentIndex]);
            setShowingStep(currentIndex);
            currentIndex++;
            setTimeout(() => {
              setShowingIndex(-1);
              setTimeout(showNext, 250);
            }, 600);
          };
          setTimeout(showNext, 800);
        }, 1500);
      }
    }
  }, [isInputEnabled, tutorialPhase, userSequence, tutorialSequence, goToGame]);

  const handleGameTilePress = useCallback((tileIndex: number) => {
    if (!isInputEnabled || isAnimatingRef.current || isShowing) return;

    const touchTime = Date.now() - roundStartTimeRef.current;
    touchTimesRef.current = [...touchTimesRef.current, touchTime];

    const newUserSequence = [...userSequence, tileIndex];
    setUserSequence(newUserSequence);

    if (newUserSequence.length === sequence.length) {
      const isCorrect = newUserSequence.every((val, idx) => val === sequence[idx]);
      const attemptDuration = Date.now() - roundStartTimeRef.current;
      setIsInputEnabled(false);

      const span = currentSpanRef.current;
      const retry = isRetryRef.current;

      const result: AttemptResult = {
        span,
        isRetry: retry,
        isSuccess: isCorrect,
        presentedSequence: [...sequence],
        userSequence: [...newUserSequence],
        touchReactionTimes: [...touchTimesRef.current],
        attemptDuration,
      };

      const newResults = [...resultsRef.current, result];
      resultsRef.current = newResults;
      setResults(newResults);

      if (isCorrect && span > maxSuccessSpanRef.current) {
        maxSuccessSpanRef.current = span;
        maxSpanIsFirstTryRef.current = !retry;
      }

      attemptCountRef.current += 1;
      const attempts = attemptCountRef.current;

      // 본 수행 최대 4회 도달 시 종료
      if (attempts >= MAX_ATTEMPTS) {
        setMessage(isCorrect ? '정확해요!' : retry ? '좋아요. 다음 단계로 넘어갈게요.' : '좋아요.');
        setTimeout(() => {
          finishGame();
        }, 1200);
        return;
      }

      if (isCorrect) {
        setMessage('정확해요!');
        setTimeout(() => {
          setSpan(Math.min(span + 1, 6));
          isRetryRef.current = false;
          setIsRetry(false);
          startAttempt();
        }, 1200);
      } else if (!retry) {
        // 1차 실패 → 동일 난이도 1회 재수행
        isRetryRef.current = true;
        setIsRetry(true);
        setMessage('한 번 더 집중해볼까요?');
        setTimeout(() => {
          startAttempt();
        }, 1500);
      } else {
        // 재수행 실패 → 다음 난이도로 상승
        isRetryRef.current = false;
        setIsRetry(false);
        setMessage('좋아요. 다음 단계로 넘어갈게요.');
        setTimeout(() => {
          setSpan(Math.min(span + 1, 6));
          startAttempt();
        }, 1500);
      }
    }
  }, [isInputEnabled, isShowing, userSequence, sequence, setSpan, startAttempt, finishGame]);

  const goToNext = useCallback(() => {
    // TODO: 기존 개발된 소음·환경 안내 페이지로 이동
  }, []);

  return {
    screen,
    currentSpan,
    stage: spanToStage(currentSpan),
    isRetry,
    attemptCount,
    sequence,
    userSequence,
    isShowing,
    isInputEnabled,
    results,
    maxSuccessSpan,
    maxSpanIsFirstTry,
    score,
    layout,
    tutorialPhase,
    tutorialSequence,
    showingIndex,
    showingStep,
    message,
    goToTutorial,
    goToGame,
    goToGameGuide,
    goToIntro,
    handleTutorialTilePress,
    handleGameTilePress,
    goToNext,
    showExitModal,
    openExitModal,
    closeExitModal,
    confirmExit,
  };
}